import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { getSymptomsRequest } from "src/redux/slices/symptomsSlice";
import moment from "moment";
import SymptomLogsBottomSheet from "../../bottom-sheet/SymptomLogsBottomSheet";
import { ChevronRight, Stethoscope } from "lucide-react";
import {
  SymptomsOnSingleDay,
  SymptomsOnTimeInterval,
} from "src/types/symptoms.types";
import Card from "../../Card/Card";
import Button from "../../Button";
import { LOGGERS_TYPES } from "src/constants/loggers";
import { bindQueryParams } from "src/helpers/tools";
import Shimmer from "src/components/common/Shimmer";
import {
  SHORT_DATE_WITH_DAY_ONLY,
  SHORT_DATE_WITH_MONTH_DAY,
  TIME_LABELS,
} from "src/constants/dataTimeFormats";

type SymptomCandleProps = {
  item: SymptomsOnSingleDay;
  index: number;
  setIsSymptomLogsBottomSheetOpen: (isOpen: boolean) => void;
  setSelectedBlockData: (data: any) => void;
};

const SymptomCandle: React.FC<SymptomCandleProps> = ({
  item,
  index,
  setIsSymptomLogsBottomSheetOpen,
  setSelectedBlockData,
}) => {
  const renderCandleContent = (
    block: SymptomsOnTimeInterval[],
    timeSlot: string
  ) => {
    switch ((block ?? []).length) {
      case 0:
        return (
          <div
            className="h-[85px] w-[52px]"
            onClick={() => {
              setIsSymptomLogsBottomSheetOpen(true);
              setSelectedBlockData(block);
            }}
          />
        );
      case 1:
        return (
          <div
            className="h-[85px] w-[52px] relative flex items-center justify-center"
            onClick={() => {
              setIsSymptomLogsBottomSheetOpen(true);
              setSelectedBlockData(block);
            }}
          >
            <div className="absolute left-[1px] min-w-[40px] min-h-[40px] flex items-center justify-center rounded-full bg-[var(--icon-bg-primary)] border-2 border-white">
              {block?.[0]?.symptoms_info?.symptom_image_url ? (
                <img
                  src={block?.[0]?.symptoms_info?.symptom_image_url}
                  alt="symptom"
                  className="h-8 w-8 object-contain z-10"
                />
              ) : (
                <Stethoscope className="h-5 w-5 text-[var(--app-primary)]" />
              )}
            </div>
          </div>
        );
      default:
        return (
          <div
            className="h-[85px] w-[52px] relative flex items-center justify-center"
            onClick={() => {
              setIsSymptomLogsBottomSheetOpen(true);
              setSelectedBlockData(block);
            }}
          >
            {/* First symptom (background) */}
            <div className="absolute left-[1px] min-w-[40px] min-h-[40px] flex items-center justify-center rounded-full bg-[var(--icon-bg-primary)] border-2 border-white z-0">
              {block?.[0]?.symptoms_info?.symptom_image_url ? (
                <img
                  src={block?.[0]?.symptoms_info?.symptom_image_url}
                  alt="symptom"
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <Stethoscope className="h-5 w-5 text-[var(--app-primary)]" />
              )}
            </div>
            {/* Second symptom (foreground) */}
            <div className="absolute left-[1px] min-w-[40px] min-h-[40px] flex items-center justify-center rounded-full bg-[var(--icon-bg-primary)] border-2 border-white z-10">
              {block?.[1]?.symptoms_info?.symptom_image_url ? (
                <img
                  src={block?.[1]?.symptoms_info?.symptom_image_url}
                  alt="symptom"
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <Stethoscope className="h-5 w-5 text-[var(--app-primary)]" />
              )}
            </div>
          </div>
        );
    }
  };

  if (!item) {
    return (
      <div className="h-64 w-16 rounded-full bg-[var(--icon-bg-primary)]" />
    );
  }

  return (
    <button
      className="flex flex-col items-center max-w-[50px] gap-1 transform -scale-x-100  border-[var(--app-primary)] border rounded-full"
      onClick={() => {
        // Handle sheet open here
      }}
    >
      <div className="h-64 w-[50px] rounded-full bg-[var(--icon-bg-primary)] p-1 flex flex-col relative">
        {renderCandleContent(item["2"], "2")}
        {renderCandleContent(item["1"], "1")}
        {renderCandleContent(item["0"], "0")}
      </div>
    </button>
  );
};

interface SymptomsChartProps {
  className?: string;
}

const SymptomsChart: React.FC<SymptomsChartProps> = ({ className }) => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const dispatch = useAppDispatch();
  const symptoms = useAppSelector((state) => state.symptoms.symptoms.data);
  const isLoading = useAppSelector(
    (state) => state.symptoms.symptoms.isLoading
  );
  const patientId = useAppSelector(
    (state) => state.auth.patient.data?.patient_id
  );
  const [isSymptomLogsBottomSheetOpen, setIsSymptomLogsBottomSheetOpen] =
    useState(false);
  const [selectedBlockData, setSelectedBlockData] = useState<
    SymptomsOnTimeInterval[]
  >([]);
  useEffect(() => {
    fetchSymptoms();
  }, []);

  const fetchSymptoms = () => {
    new Promise((resolve, reject) => {
      dispatch(
        getSymptomsRequest({
          resolve,
          reject,
          payload: {
            page: 1,
            patient_id: patientId || "c6fbab03-4a8a-11f0-ab23-0022486e34ba", //"e2d89a1d-91c7-11ef-9117-0022486e34ba", //"3bff0ba2-e1d1-11ee-ae7b-0022486e34ba",
            // "symptoms_id": "0aa32e7c-f0b4-47f0-9dcf-37068857fb5a",
            // "visit_id" : "04f3cb92-3182-4613-8e5a-fd1554ab6859",
            // motivational_quotes : true
          },
        })
      );
    }).then((res) => {
      // Response handled silently
    });
  };

  const handleStartLoggingClick = (loggerId: string) => {
    const loggerType = LOGGERS_TYPES[loggerId as keyof typeof LOGGERS_TYPES];
    const url = bindQueryParams(
      `/app/${supportProgramKey}/health-assistant-screen`,
      {
        loggerType,
      }
    );
    navigate(url);
  };

  return (
    <div className={className}>
      {isLoading ? (
        <Shimmer width="100%" height="295px" />
      ) : (
        <Card
          title="Symptoms Trends"
          description="Track your daily symptoms and identify patterns over time"
          icon={<Stethoscope className="w-5 h-5 text-secondary" />}
          rightIcon={<ChevronRight className="w-4 h-4" />}
          rightIconClick={() => {
            navigate(`/app/${supportProgramKey}/symptom-logs`);
          }}
        >
          {/* Chart Container */}
          <div className="flex">
            {/* Y-Axis Time Labels */}

            {/* Single Scrollable Container for Chart + X-Axis */}
            {[...(symptoms?.symptoms || [])].length > 0 ? (
              <>
                <div className="bg-[var(--icon-bg-primary)] border-r border-[var(--app-primary)] rounded-l-lg p-2 flex flex-col justify-between h-64">
                  {TIME_LABELS.map((time, index) => (
                    <div
                      key={index}
                      className="text-sm font-medium text-gray-900"
                    >
                      {time}
                    </div>
                  ))}
                </div>

                <div className="flex-1 overflow-x-auto min-w-0 ml-3">
                  {/* Chart Candlesticks */}
                  <div className="flex space-x-3.5 w-max mb-2">
                    {[...(symptoms?.symptoms || [])].map(
                      (dayData: SymptomsOnSingleDay, dayIndex: number) => {
                        // Extract all symptoms from this day and group them by actual hour ranges
                        // const allSymptoms: any[] = [];

                        // // Process each hour's symptoms
                        // Object.keys(dayData).forEach((key) => {
                        //   if (key !== "date" && Array.isArray(dayData[key])) {
                        //     allSymptoms.push(...dayData[key]);
                        //   }
                        // });

                        // // Group symptoms by actual hour ranges (0-8, 8-16, 16-24)
                        // const timeSlot0: any[] = []; // 0-8 hours (bottom division)
                        // const timeSlot1: any[] = []; // 8-16 hours (middle division)
                        // const timeSlot2: any[] = []; // 16-24 hours (top division)

                        // allSymptoms.forEach((symptom) => {
                        //   const hour = symptom.hour;

                        //   if (hour >= 0 && hour < 8) {
                        //     timeSlot0.push(symptom);
                        //   } else if (hour >= 8 && hour < 16) {
                        //     timeSlot1.push(symptom);
                        //   } else if (hour >= 16 && hour < 24) {
                        //     timeSlot2.push(symptom);
                        //   }
                        // });

                        return (
                          <div
                            key={dayIndex}
                            className="w-[50px] flex-shrink-0 flex justify-center"
                          >
                            {
                              <SymptomCandle
                                item={dayData}
                                index={2}
                                setIsSymptomLogsBottomSheetOpen={
                                  setIsSymptomLogsBottomSheetOpen
                                }
                                setSelectedBlockData={setSelectedBlockData}
                              />
                            }
                          </div>
                        );
                      }
                    )}
                  </div>

                  {/* X-Axis Date Labels */}
                  <div className="bg-[var(--icon-bg-primary)] border-t border-[var(--app-primary)] rounded-b-lg p-2 px-1 w-max">
                    <div className="flex space-x-3.5">
                      {[...(symptoms?.symptoms || [])]
                        //.reverse()
                        .map((dayData: any, dayIndex: number) => {
                          return (
                            <div
                              key={dayIndex}
                              className="w-[50px] flex-shrink-0 text-center"
                            >
                              <div className="text-xs font-medium text-gray-900">
                                {moment(dayData?.date).format(
                                  SHORT_DATE_WITH_MONTH_DAY
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {moment(dayData?.date).format(
                                  SHORT_DATE_WITH_DAY_ONLY
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-y-4 text-center">
                <div className="w-12 h-12 flex items-center justify-center p-2 bg-[var(--bg-secondary)] rounded-full">
                  <Stethoscope className="w-5 h-5 text-[var(--app-primary)]" />
                </div>
                <span className="text-base font-medium text-gray-500">
                  No Data Logged
                </span>
                <span className="text-sm font-light text-gray-500">
                  Start tracking your symptoms logs to see insights and patterns
                  over time.
                </span>
                <Button
                  variant="contained"
                  rounded="full"
                  onClick={() => handleStartLoggingClick("symptom-logs")}
                >
                  Start Logging
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}
      {isSymptomLogsBottomSheetOpen && selectedBlockData?.length > 0 && (
        <SymptomLogsBottomSheet
          showBottomSheet={isSymptomLogsBottomSheetOpen}
          setShowBottomSheet={setIsSymptomLogsBottomSheetOpen}
          selectedBlockData={selectedBlockData}
        />
      )}
    </div>
  );
};

export default SymptomsChart;
